// A refresh that fails is not always a lost session. The server rejecting the
// refresh token (401/403) ends the session; a network error or a 5xx while the
// backend restarts (devloop swaps the binary in a few seconds) does not: the
// tokens stay and the next attempt retries.

export class RefreshRejectedError extends Error {
    status: number
    // The backend's problem-detail code: session_evicted, session_expired,
    // INVALID_TOKEN. Undefined when the body carried none.
    code?: string
    constructor(status: number, code?: string) {
        super(`refresh token rejected (${status}${code ? `, ${code}` : ''})`)
        this.status = status
        this.code = code
        this.name = 'RefreshRejectedError'
    }
}

// Why the server ended the session, as the login page explains it. Anything
// else (an unknown or revoked token) gets the generic "session expired".
export type SessionEndedReason = 'session_evicted' | 'session_expired'

export function sessionEndedReason(err: unknown): SessionEndedReason | null {
    if (!(err instanceof RefreshRejectedError)) return null
    return err.code === 'session_evicted' || err.code === 'session_expired' ? err.code : null
}

export function parseSessionEndedReason(raw: unknown): SessionEndedReason | null {
    return raw === 'session_evicted' || raw === 'session_expired' ? raw : null
}

export class RefreshTransientError extends Error {
    status?: number
    constructor(message: string, status?: number) {
        super(message)
        this.status = status
        this.name = 'RefreshTransientError'
    }
}

// post is apiClient.POST narrowed to what the refresh needs. It is the raw
// client, not the useApi wrapper, because the wrapper throws on any error body
// and loses the HTTP status that tells a rejection from an outage.
type PostFn = (path: string, options: { params: Record<string, unknown>; body: unknown }) => Promise<unknown>

interface RefreshResponse {
    data?: unknown
    error?: unknown
    response?: { status: number }
}

// Delays before the short retries that follow a transient failure.
export const TRANSIENT_RETRY_DELAYS_MS = [2000, 5000, 10000]

// callRefresh posts the refresh token and returns the response body. It throws
// RefreshRejectedError when the server refuses the token and
// RefreshTransientError for everything else that goes wrong.
export async function callRefresh(post: PostFn, orgCode: string | null, refreshToken: string): Promise<unknown> {
    let res: RefreshResponse
    try {
        res = (await post('/api/v1/organizations/{orgCode}/auth/refresh', {
            params: { path: { orgCode } },
            body: { refresh_token: refreshToken },
        })) as RefreshResponse
    } catch (err) {
        // fetch rejects on a network failure; 
        throw new RefreshTransientError(err instanceof Error ? err.message : 'network error')
    }
    const status = res.response?.status
    if (status === 401 || status === 403) {
        const code = (res.error as { code?: unknown } | undefined)?.code
        throw new RefreshRejectedError(status, typeof code === 'string' ? code : undefined)
    }
    if (status !== undefined && status >= 400) throw new RefreshTransientError(`refresh failed (${status})`, status)
    if (res.error || res.data === undefined || res.data === null || res.data === '') {
        throw new RefreshTransientError('refresh answered without a body', status)
    }
    return res.data
}

// withTransientRetry runs fn and, while it fails with a RefreshTransientError,
// retries after each delay. A rejection or success returns at once.
export async function withTransientRetry<T>(
    fn: () => Promise<T>,
    delays: number[] = TRANSIENT_RETRY_DELAYS_MS,
    sleep: (ms: number) => Promise<void> = (ms) => new Promise((r) => setTimeout(r, ms)),
): Promise<T> {
    for (let i = 0; ; i++) {
        try {
            return await fn()
        } catch (err) {
            if (!(err instanceof RefreshTransientError) || i >= delays.length) throw err
            await sleep(delays[i])
        }
    }
}

// siblingRefreshToken returns the refresh token another tab of this browser
// stored (localStorage `authState`) when it differs from the one this tab
// used: the user signed in again in that tab, and this tab's token may have
// been ended by it. Null when storage holds no session or the same token.
export function siblingRefreshToken(stored: string | null, usedToken: string): string | null {
    if (!stored) return null
    try {
        const parsed = JSON.parse(stored) as { isAuthenticated?: unknown; refreshToken?: unknown }
        if (parsed.isAuthenticated !== true || typeof parsed.refreshToken !== 'string') return null
        return parsed.refreshToken && parsed.refreshToken !== usedToken ? parsed.refreshToken : null
    } catch {
        return null
    }
}

// refreshWithSiblingFallback refreshes with token and, when the server rejects
// it while a sibling tab already stored a newer session, retries once with
// that one: a tab never logs out on a token another tab replaced. It resolves
// with the response body and the refresh token that worked. A transient
// failure is thrown as is, without trying the sibling's token.
export async function refreshWithSiblingFallback(
    refresh: (refreshToken: string) => Promise<unknown>,
    token: string,
    readStored: () => string | null,
): Promise<{ data: unknown; refreshToken: string }> {
    try {
        return { data: await refresh(token), refreshToken: token }
    } catch (err) {
        if (!(err instanceof RefreshRejectedError)) throw err
        const sibling = siblingRefreshToken(readStored(), token)
        if (!sibling) throw err
        return { data: await refresh(sibling), refreshToken: sibling }
    }
}
